import { Component, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from './core/services/auth.service';
import { NavigationService } from './core/services/navigation.service';
import { LoginComponent } from './features/auth/login/login.component';
import { ProjectDetailComponent } from './features/project-detail/project-detail.component';
import { Project } from './core/models';
import { LanguageService } from './core/services/language.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    LoginComponent,
    ProjectDetailComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  readonly authService = inject(AuthService);
  readonly nav = inject(NavigationService);
  readonly language = inject(LanguageService);
  private readonly router = inject(Router);
  readonly isNavigating = signal(false);
  readonly helpOpen = signal(false);
  private readonly currentUrl = signal(this.router.url);

  get user() { return this.authService.user; }
  get isAuthenticated() { return this.authService.isAuthenticated; }
  get isLoading$() { return this.authService.isLoading$; }
  get selectedProject() { return this.nav.selectedProject; }

  userInitials = computed(() => {
    const name = this.authService.user?.name ?? this.authService.user?.email ?? '';
    return name.split(/[\s.@]+/).filter(Boolean).slice(0, 2)
      .map(p => p[0].toUpperCase()).join('') || '?';
  });

  readonly help = computed(() => {
    if (this.selectedProject()) {
      return {
        title: 'Aide — Détail du projet',
        description: 'Cette page permet de suivre les livrables du projet sélectionné et de maintenir leur état à jour.',
        items: [
          'Utilisez les filtres pour cibler une phase, un statut ou les éléments en retard.',
          'Sélectionnez un livrable pour consulter son exigence, son aide, ses liens et son historique.',
          'La progression compte seulement les éléments applicables; un livrable incomplet d’une phase antérieure est signalé en retard.',
          'Les options du projet permettent notamment de modifier ses renseignements, son référentiel ou son archivage.',
        ],
      };
    }

    switch (this.currentUrl()) {
      case '/reports':
        return {
          title: 'Aide — Rapports',
          description: 'Cette page résume l’état des projets actifs pour faciliter le suivi du portefeuille.',
          items: [
            'Les indicateurs présentent la progression moyenne, les livrables en retard et les projets en problème.',
            'La section « Par site » compare le volume de projets, leur progression et leurs retards.',
            'Les projets prioritaires regroupent ceux ayant des retards, un problème ou une progression sous 50 %.',
            'Utilisez « Imprimer » pour produire une version imprimable du rapport affiché.',
          ],
        };
      case '/settings':
        return {
          title: 'Aide — Paramètres',
          description: 'Cette page sert à administrer les référentiels de livrables, les types de projet et les sites.',
          items: [
            'Sélectionnez une version du référentiel; seules les versions brouillon peuvent être modifiées.',
            'Ajoutez ou ajustez les livrables par phase, puis enregistrez ou publiez le brouillon lorsque prêt.',
            'Une version publiée peut être utilisée par les projets; les projets existants ne sont pas modifiés automatiquement.',
            'L’onglet « Gérer les types et sites » permet de maintenir les choix offerts à la création des projets.',
          ],
        };
      case '/profile':
        return {
          title: 'Aide — Mon profil',
          description: 'Cette page affiche les renseignements associés à votre session.',
          items: [
            'Votre nom et votre adresse courriel proviennent de votre compte d’authentification.',
            'Les groupes indiqués déterminent les accès qui vous sont accordés dans l’application.',
            'Pour toute modification à votre compte ou à vos accès, communiquez avec le soutien approprié.',
          ],
        };
      default:
        return {
          title: 'Aide — Portefeuille',
          description: 'Cette page permet de consulter, filtrer et créer les projets du portefeuille.',
          items: [
            'Utilisez la recherche et les filtres pour trouver les projets par numéro RTBS, site, phase ou statut.',
            'Sélectionnez un projet pour ouvrir son index de livrables et suivre sa progression.',
            'Créez un projet avec « Nouveau projet »; l’aide au choix du processus vous guide pour sélectionner le cadre.',
            'Les indicateurs mettent en évidence les projets actifs, les retards et les livrables bloqués.',
          ],
        };
    }
  });

  ngOnInit(): void {
    this.language.startDomTranslation();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isNavigating.set(true);
        this.closeProject();
      }

      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.isNavigating.set(false);
      }

      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects);
        this.closeHelp();
      }
    });
  }

  closeProject(): void { this.nav.close(); }
  openHelp(): void { this.helpOpen.set(true); }
  closeHelp(): void { this.helpOpen.set(false); }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.closeHelp(); }

  onProjectUpdated(updated: Project): void { this.nav.update(updated); }

  async logout(): Promise<void> {
    await this.authService.logout();
  }
}
